import React from 'react';
import { FiStar, FiUsers, FiZap, FiLock, FiTrendingUp, FiSliders, FiGlobe, FiCheck, FiX, FiRotateCcw, FiChevronDown } from 'react-icons/fi';
import '../Marketplace.css';

// Punto metálico por tier (reemplaza 🥇🥈🥉) — usa --fin-gold/silver/bronze
const LEVEL_META = {
  all:    { label: 'Todos',  color: null },
  new:    { label: 'Nuevo',  color: 'var(--mk-text-tertiary)' },
  bronze: { label: 'Bronce', color: 'var(--fin-bronze)' },
  silver: { label: 'Plata',  color: 'var(--fin-silver)' },
  gold:   { label: 'Oro',    color: 'var(--fin-gold)' },
};

function LevelDot({ color }) {
  if (!color) return <FiGlobe size={12} className="mk-af-globe" />;
  return <span className="mk-af-metal-dot" style={{ background: color }} aria-hidden="true" />;
}

/**
 * Advanced Filters Component for Marketplace
 * Provides filtering by:
 * - Minimum rating (0-5 stars)
 * - Minimum completed trades (0-500)
 * - Maximum response time (1-120 minutes)
 * - Verified traders only
 * - Trader level (new, bronze, silver, gold)
 */
export default function AdvancedFilters({
  filters,
  onFiltersChange,
  matchingCount,
  totalCount,
}) {
  const handleChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const getTraderlevelDescription = (trades) => {
    if (trades === 0) return 'Sin trades completados';
    if (trades < 50) return 'Menos de 50 trades';
    if (trades < 200) return '50-199 trades';
    return '200+ trades';
  };

  return (
    <div className="mk-advanced-filters">
      <details className="mk-filters-group">
        <summary className="mk-filters-toggle">
          <span className="mk-filters-toggle-label">
            <FiSliders size={18} />
            Filtros Avanzados
          </span>
          <span className="mk-filters-toggle-right">
            <span className="mk-filters-toggle-count"><span className="fin-num">{matchingCount}</span> ofertas</span>
            <FiChevronDown size={18} className="mk-filters-toggle-chev" />
          </span>
        </summary>

        <div className="mk-filters-panel">
          {/* RATING FILTER */}
          <div className="mk-filter-item">
            <label className="mk-filter-label">
              <FiStar size={16} style={{ marginRight: '8px' }} />
              Rating Mínimo
              <span className="mk-filter-value"><span className="fin-num">{filters.minRating.toFixed(1)}</span> estrellas</span>
            </label>
            <input
              type="range"
              className="mk-filter-slider"
              min="0"
              max="5"
              step="0.5"
              value={filters.minRating}
              onChange={(e) => handleChange('minRating', parseFloat(e.target.value))}
            />
            <div className="mk-filter-scale">
              <span>0</span>
              <span>2.5</span>
              <span>5</span>
            </div>
          </div>

          {/* TRADES FILTER */}
          <div className="mk-filter-item">
            <label className="mk-filter-label">
              <FiUsers size={16} style={{ marginRight: '8px' }} />
              Mínimo de Trades
              <span className="mk-filter-value"><span className="fin-num">{filters.minTrades}</span> trades</span>
            </label>
            <input
              type="range"
              className="mk-filter-slider"
              min="0"
              max="500"
              step="10"
              value={filters.minTrades}
              onChange={(e) => handleChange('minTrades', parseInt(e.target.value))}
            />
            <div className="mk-filter-scale">
              <span>0</span>
              <span>250</span>
              <span>500</span>
            </div>
          </div>

          {/* RESPONSE TIME FILTER */}
          <div className="mk-filter-item">
            <label className="mk-filter-label">
              <FiZap size={16} style={{ marginRight: '8px' }} />
              Máx. Tiempo de Respuesta
              <span className="mk-filter-value"><span className="fin-num">{filters.maxResponseTime}</span> min</span>
            </label>
            <input
              type="range"
              className="mk-filter-slider"
              min="1"
              max="120"
              step="5"
              value={filters.maxResponseTime}
              onChange={(e) => handleChange('maxResponseTime', parseInt(e.target.value))}
            />
            <div className="mk-filter-scale">
              <span>1 min</span>
              <span>60 min</span>
              <span>120+ min</span>
            </div>
          </div>

          {/* VERIFIED TOGGLE */}
          <div className="mk-filter-item mk-filter-toggle-item">
            <label className="mk-filter-label mk-filter-checkbox">
              <input
                type="checkbox"
                checked={filters.verifiedOnly}
                onChange={(e) => handleChange('verifiedOnly', e.target.checked)}
              />
              <FiLock size={16} style={{ marginRight: '8px' }} />
              <span>Solo Vendedores Verificados</span>
            </label>
            <p className="mk-filter-description">
              Mostrar solo ofertas de vendedores con verificación confirmada
            </p>
          </div>

          {/* TRADER LEVEL SELECT */}
          <div className="mk-filter-item">
            <label className="mk-filter-label">
              <FiTrendingUp size={16} style={{ marginRight: '8px' }} />
              Nivel de Trader
            </label>
            <select
              className="mk-filter-select"
              value={filters.traderLevel}
              onChange={(e) => handleChange('traderLevel', e.target.value)}
            >
              <option value="all">Todos los niveles</option>
              <option value="new">Nuevo · 0 trades</option>
              <option value="bronze">Bronce · 1-49 trades</option>
              <option value="silver">Plata · 50-199 trades</option>
              <option value="gold">Oro · 200+ trades</option>
            </select>

            {/* Tier seleccionado — punto metálico (reemplaza el emoji del <option>) */}
            <div className="mk-af-level-chips">
              {['all', 'new', 'bronze', 'silver', 'gold'].map((lvl) => (
                <button
                  type="button"
                  key={lvl}
                  className={`fin-chip mk-af-level-chip ${filters.traderLevel === lvl ? 'active' : ''}`}
                  onClick={() => handleChange('traderLevel', lvl)}
                >
                  <LevelDot color={LEVEL_META[lvl].color} />
                  {LEVEL_META[lvl].label}
                </button>
              ))}
            </div>
            <p className="mk-filter-description">
              {getTraderlevelDescription(
                filters.traderLevel === 'new'
                  ? 0
                  : filters.traderLevel === 'bronze'
                  ? 25
                  : filters.traderLevel === 'silver'
                  ? 100
                  : filters.traderLevel === 'gold'
                  ? 250
                  : 0
              )}
            </p>
          </div>

          {/* RESULTS SUMMARY */}
          <div className="mk-filters-summary">
            <p className="mk-filters-match-count">
              {matchingCount === 0 ? (
                <>
                  <span className="mk-no-match"><FiX className="mk-af-result-ic" /> No hay ofertas</span>
                  que coincidan con tus filtros
                </>
              ) : (
                <>
                  <span className="mk-match-number"><FiCheck className="mk-af-result-ic" /> <span className="fin-num">{matchingCount}</span></span> ofertas
                  coinciden {matchingCount !== totalCount && <>de <span className="fin-num">{totalCount}</span></>}
                </>
              )}
            </p>
          </div>

          {/* RESET BUTTON */}
          <button
            className="mk-filters-reset"
            onClick={() =>
              onFiltersChange({
                minRating: 0,
                minTrades: 0,
                maxResponseTime: 120,
                verifiedOnly: false,
                traderLevel: 'all',
              })
            }
          >
            <FiRotateCcw size={14} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
            Restablecer filtros
          </button>
        </div>
      </details>
    </div>
  );
}
